import type { CatalogItem } from '@/types/marketplace'

export type SearchableCatalogItem = Pick<CatalogItem, 'slug' | 'displayName' | 'description' | 'tags'> & {
  segments: { skuTitle: string; msPartNumber?: string }[]
}

/** Keeps every product whose text holds all the terms, best match first */
export function searchCatalog<T extends SearchableCatalogItem>(products: T[], query: string): T[] {
  const phrase = query.trim().toLowerCase().replace(/\s+/g, ' ')
  if (!phrase) return products
  const terms = phrase.split(' ')
  const ranked: { product: T; tier: number }[] = []
  for (const product of products) {
    const tier = matchTier(product, phrase, terms)
    if (tier !== null) ranked.push({ product, tier })
  }
  // Stable sort, so a tier keeps the catalog's name order
  return ranked.sort((a, b) => a.tier - b.tier).map((r) => r.product)
}

// SKU titles count as names because distributor wording like "GCCH" or "G5" only appears there
function matchTier(p: SearchableCatalogItem, phrase: string, terms: string[]): number | null {
  const names = [p.displayName, ...p.segments.map((s) => s.skuTitle)].map((n) => n.toLowerCase())
  // Hyphens are dropped on both sides so "aau59343" still finds AAU-59343
  const partNumbers = p.segments.flatMap((s) => (s.msPartNumber ? [unhyphenate(s.msPartNumber.toLowerCase())] : []))
  if (names.includes(phrase) || partNumbers.includes(unhyphenate(phrase))) return 0
  if (names.some((n) => n.startsWith(phrase))) return 1
  const nameBlob = names.join(' ')
  if (terms.every((t) => nameBlob.includes(t))) return 2
  const blob = [nameBlob, p.description, ...p.tags, p.slug.replace(/-/g, ' ')].join(' ').toLowerCase()
  // Prefix only, since a short term like "e5" sits inside unrelated part numbers such as "7E5-00002"
  return terms.every((t) => blob.includes(t) || partNumbers.some((n) => n.startsWith(unhyphenate(t)))) ? 3 : null
}

function unhyphenate(text: string): string {
  return text.replace(/-/g, '')
}
